import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useAnalytics } from "@/hooks/use-analytics";

// Eager load critical pages
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load all other pages for code splitting
const GetRecommendation = lazy(() => import("./pages/GetRecommendation"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const BehavioralHealthEMR101 = lazy(() => import("./pages/BehavioralHealthEMR101"));
const EMRBenefits = lazy(() => import("./pages/EMRBenefits"));
const HowToChooseEMR = lazy(() => import("./pages/HowToChooseEMR"));
const TopEMRSolutions = lazy(() => import("./pages/TopEMRSolutions"));
const EMRImplementation = lazy(() => import("./pages/EMRImplementation"));
const EMRComparison = lazy(() => import("./pages/EMRComparison"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const FreeGuide = lazy(() => import("./pages/FreeGuide"));
const TherapyNotesDetail = lazy(() => import("./pages/TherapyNotesDetail"));
const SimplePracticeDetail = lazy(() => import("./pages/SimplePracticeDetail"));
const OpusDetail = lazy(() => import("./pages/OpusDetail"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BestEHRSoftware = lazy(() => import("./pages/blog/BestEHRSoftware"));
const BestRCMSoftware = lazy(() => import("./pages/blog/BestRCMSoftware"));
const BestHealthcareCRM = lazy(() => import("./pages/blog/BestHealthcareCRM"));
const BestEHRBehavioralHealth = lazy(() => import("./pages/blog/BestEHRBehavioralHealth"));
const BestEHRPsychiatry = lazy(() => import("./pages/blog/BestEHRPsychiatry"));
const BestEHRMentalHealthClinics = lazy(() => import("./pages/blog/BestEHRMentalHealthClinics"));
const BestEHRAddictionTreatment = lazy(() => import("./pages/blog/BestEHRAddictionTreatment"));
const BestEHRSubstanceAbuse = lazy(() => import("./pages/blog/BestEHRSubstanceAbuse"));
const BestEHRSoloPractices = lazy(() => import("./pages/blog/BestEHRSoloPractices"));
const DoxyDetail = lazy(() => import("./pages/DoxyDetail"));
const KipuDetail = lazy(() => import("./pages/KipuDetail"));
const ValantDetail = lazy(() => import("./pages/ValantDetail"));
const EpicDetail = lazy(() => import("./pages/EpicDetail"));
const AthenahealthDetail = lazy(() => import("./pages/AthenahealthDetail"));
const NextGenDetail = lazy(() => import("./pages/NextGenDetail"));
const KareoDetail = lazy(() => import("./pages/KareoDetail"));
const AdminBlogTools = lazy(() => import("./pages/AdminBlogTools"));
const Sitemap = lazy(() => import("./pages/Sitemap"));

const queryClient = new QueryClient();

const AppContent = () => {
  useAnalytics();
  return null;
};

const App = () => {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => setMounted(true), []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {mounted && <Toaster />}
        {mounted && <Sonner />}
        <BrowserRouter>
          <AppContent />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center">Loading...</div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/get-recommendation" element={<GetRecommendation />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/comparisons" element={<EMRComparison />} />
              <Route path="/resources/behavioral-health-emr-101" element={<BehavioralHealthEMR101 />} />
              <Route path="/resources/emr-benefits" element={<EMRBenefits />} />
              <Route path="/resources/how-to-choose-emr" element={<HowToChooseEMR />} />
              <Route path="/resources/top-emr-solutions" element={<TopEMRSolutions />} />
              <Route path="/resources/emr-implementation" element={<EMRImplementation />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/free-guide" element={<FreeGuide />} />
              <Route path="/emr/therapynotes" element={<TherapyNotesDetail />} />
              <Route path="/emr/simplepractice" element={<SimplePracticeDetail />} />
              <Route path="/emr/opus" element={<OpusDetail />} />
              <Route path="/emr/doxy" element={<DoxyDetail />} />
              <Route path="/emr/kipu" element={<KipuDetail />} />
              <Route path="/emr/valant" element={<ValantDetail />} />
              <Route path="/emr/epic" element={<EpicDetail />} />
              <Route path="/emr/athenahealth" element={<AthenahealthDetail />} />
              <Route path="/emr/nextgen" element={<NextGenDetail />} />
              <Route path="/emr/kareo" element={<KareoDetail />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/category/:categoryKey" element={<BlogCategory />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/blog/best-ehr-emr-software" element={<BestEHRSoftware />} />
              <Route path="/blog/best-rcm-software" element={<BestRCMSoftware />} />
              <Route path="/blog/best-healthcare-crm-software" element={<BestHealthcareCRM />} />
              <Route path="/blog/best-ehr-behavioral-health" element={<BestEHRBehavioralHealth />} />
              <Route path="/blog/best-ehr-psychiatry" element={<BestEHRPsychiatry />} />
              <Route path="/blog/best-ehr-mental-health-clinics" element={<BestEHRMentalHealthClinics />} />
              <Route path="/blog/best-ehr-addiction-treatment" element={<BestEHRAddictionTreatment />} />
              <Route path="/blog/best-ehr-substance-abuse" element={<BestEHRSubstanceAbuse />} />
              <Route path="/blog/best-ehr-solo-practices" element={<BestEHRSoloPractices />} />
              <Route path="/admin/blog-tools" element={<AdminBlogTools />} />
              <Route path="/sitemap" element={<Sitemap />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
